import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const Invoice = () => {
  const location = useLocation();
  const quote = location.state?.quote;
  const client = location.state?.client;

  // Pre-fill company info (placeholder, replace with your actual company info source)
  const companyInfo = {
    name: 'Your Company Name',
    address: '123 Main St, City, Country',
    phone: '(555) 555-5555',
    email: 'info@company.com',
  };

  // Pre-fill client info from navigation state or blank
  const [clientInfo] = useState({
    name: client?.name || '',
    address: client?.address || '',
  });

  // Pre-fill invoice number (could be generated or blank)
  const [invoiceNumber, setInvoiceNumber] = useState('');
  // Pre-fill issue date as today
  const [issueDate, setIssueDate] = useState(new Date().toISOString().slice(0, 10));
  // Pre-fill due date as blank
  const [dueDate, setDueDate] = useState('');

  // Pre-fill line items from quote if available
  const initialLineItems = quote?.materials?.length
    ? quote.materials.map(mat => ({
        description: mat.name,
        quantity: mat.qty,
        unitPrice: mat.price,
        total: mat.qty * mat.price,
      }))
    : [{ description: '', quantity: 1, unitPrice: 0, total: 0 }];
  const [lineItems, setLineItems] = useState(initialLineItems);

  // Pre-fill HST from quote if available
  const [hst, setHst] = useState(quote?.hst || '');
  const [subtotal, setSubtotal] = useState(0);
  const [total, setTotal] = useState(0);

  // State for toggling Quantity & Unit Price columns
  const [showQtyUnit, setShowQtyUnit] = useState(true);

  // Calculate subtotal and total when line items or HST change
  useEffect(() => {
    const sub = lineItems.reduce((sum, item) => sum + (showQtyUnit ? (item.quantity * item.unitPrice) : (item.total || 0)), 0);
    setSubtotal(sub);
    const hstValue = parseFloat(hst) || 0;
    setTotal(sub + hstValue);
  }, [lineItems, hst, showQtyUnit]);

  // Placeholder for live PDF preview logic

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white rounded-xl shadow">
      <h1 className="text-2xl font-bold mb-4">Create Invoice</h1>
      <div className="mb-4">
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={showQtyUnit}
            onChange={() => setShowQtyUnit(!showQtyUnit)}
          />
          <span>Show Quantity & Unit Price Columns</span>
        </label>
      </div>
      {/* Company Info */}
      <div className="mb-4">
        <h2 className="font-semibold">{companyInfo.name}</h2>
        <div className="text-sm text-gray-600">{companyInfo.streetAddress}</div>
        <div className="text-sm text-gray-600">{companyInfo.city}, {companyInfo.province} {companyInfo.postalCode}</div>
        <div className="text-sm text-gray-600">{companyInfo.phone} | {companyInfo.email}</div>
      </div>
      {/* Client Info and Invoice Details */}
      <div className="flex justify-between mb-4">
        <div>
          <div className="font-semibold">Bill To:</div>
          <div>{clientInfo.name}</div>
          <div className="text-sm text-gray-600">{clientInfo.address}</div>
        </div>
        <div className="text-right">
          <div className="font-semibold">Invoice #</div>
          <input
            type="text"
            value={invoiceNumber}
            onChange={e => setInvoiceNumber(e.target.value)}
            className="border rounded px-2 py-1 w-28 mb-1"
            placeholder="Auto"
          />
          <div className="font-semibold">Issued</div>
          <input
            type="date"
            value={issueDate}
            onChange={e => setIssueDate(e.target.value)}
            className="border rounded px-2 py-1 w-28 mb-1"
          />
          <div className="font-semibold">Due</div>
          <input
            type="date"
            value={dueDate}
            onChange={e => setDueDate(e.target.value)}
            className="border rounded px-2 py-1 w-28"
          />
        </div>
      </div>
      {/* Line Items Table */}
      <div className="mb-4">
        <table className="w-full border rounded">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-2 text-left">Description</th>
              {showQtyUnit && <th className="p-2 text-right">Qty</th>}
              {showQtyUnit && <th className="p-2 text-right">Unit Price</th>}
              <th className="p-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {lineItems.map((item, idx) => (
              <tr key={idx}>
                <td className="p-2">
                  <input
                    type="text"
                    value={item.description}
                    onChange={e => {
                      const newItems = [...lineItems];
                      newItems[idx].description = e.target.value;
                      setLineItems(newItems);
                    }}
                    className="border rounded px-2 py-1 w-full"
                  />
                </td>
                {showQtyUnit && (
                  <td className="p-2 text-right">
                    <input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={e => {
                        const newItems = [...lineItems];
                        newItems[idx].quantity = Number(e.target.value);
                        newItems[idx].total = newItems[idx].quantity * newItems[idx].unitPrice;
                        setLineItems(newItems);
                      }}
                      className="border rounded px-2 py-1 w-16 text-right"
                    />
                  </td>
                )}
                {showQtyUnit && (
                  <td className="p-2 text-right">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.unitPrice}
                      onChange={e => {
                        const newItems = [...lineItems];
                        newItems[idx].unitPrice = Number(e.target.value);
                        newItems[idx].total = newItems[idx].quantity * newItems[idx].unitPrice;
                        setLineItems(newItems);
                      }}
                      className="border rounded px-2 py-1 w-20 text-right"
                    />
                  </td>
                )}
                <td className="p-2 text-right">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={showQtyUnit ? item.total : item.total}
                    onChange={e => {
                      const newItems = [...lineItems];
                      newItems[idx].total = Number(e.target.value);
                      setLineItems(newItems);
                    }}
                    className="border rounded px-2 py-1 w-24 text-right"
                    disabled={showQtyUnit}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button
          className="mt-2 px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 text-sm"
          onClick={() => setLineItems([...lineItems, { description: '', quantity: 1, unitPrice: 0, total: 0 }])}
        >
          + Add Line Item
        </button>
      </div>
      {/* Summary Section */}
      <div className="flex flex-col items-end mb-4">
        <div className="flex gap-8">
          <div className="text-right">
            <div>Subtotal: ${subtotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            <div className="flex items-center gap-2">
              <span>HST:</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={hst}
                onChange={e => setHst(e.target.value)}
                className="border rounded px-2 py-1 w-20 text-right"
                placeholder="0.00"
              />
            </div>
            <div className="font-bold">Total: ${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
        </div>
      </div>
      {/* Thank You Footer */}
      <div className="text-center text-gray-600 mt-8">Thank you for your business!</div>
      {/* Live PDF Preview Placeholder */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold mb-2">Live Invoice PDF Preview</h2>
        <div className="border rounded p-4 bg-gray-50 text-center text-gray-400">
          (PDF preview will appear here as you fill out the form)
        </div>
      </div>
    </div>
  );
};

export default Invoice; 